import React, { useEffect, useRef, useState } from "react";
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EmployeeAutocomplete from "../../component/common/EmployeeAutocomplete";
import ActionButton from "../../component/common/ActionButton";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import BeachAccessRoundedIcon from "@mui/icons-material/BeachAccessRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import FlightRoundedIcon from "@mui/icons-material/FlightRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";

import { api } from "../../util/api";
import { notify } from "../../util/toast";
import { approvalApi } from "./api/approvalApi";
import dayjs from "dayjs";
import { applyRangeEndChange, applyRangeStartChange } from "../../util/dateUtils";
import FormField from "../../component/common/FormField";
import ComboBox from "../../component/common/ComboBox";
import { getApiErrorMessage } from "../../util/apiFeedback";
import VendorAutocomplete from "../finance/components/vendor/VendorAutocomplete";
import { channelApi } from "../channel/api/channelApi";

const BASE = process.env.REACT_APP_API_REAL_URL;

const FORM_TYPES = [
    { type: "VACATION", label: "휴가신청서", icon: <BeachAccessRoundedIcon sx={{ fontSize: 32 }} />, color: "#3b82f6" },
    { type: "PAYMENT", label: "대금지급요청", icon: <PaymentsRoundedIcon sx={{ fontSize: 32 }} />, color: "#10b981" },
    { type: "PURCHASE", label: "구매품의서", icon: <ShoppingCartRoundedIcon sx={{ fontSize: 32 }} />, color: "#f59e0b" },
    { type: "BUSINESS_TRIP", label: "출장신청서", icon: <FlightRoundedIcon sx={{ fontSize: 32 }} />, color: "#3b82f6" },
    { type: "OVERTIME", label: "초과근무신청", icon: <AccessTimeRoundedIcon sx={{ fontSize: 32 }} />, color: "#ef4444" },
    { type: "HOLIDAY_WORK", label: "휴일근무신청", icon: <EventBusyRoundedIcon sx={{ fontSize: 32 }} />, color: "#9333ea" },
    { type: "OTHER", label: "기타결재", icon: <AssignmentRoundedIcon sx={{ fontSize: 32 }} />, color: "#6b7280" },
];

const REQUIRED_FIELDS = {
    VACATION: ["vacationType", "startDate", "endDate", "reason"],
    PAYMENT: ["vendor", "amount", "paymentDate", "description"],
    PURCHASE: ["item", "quantity", "amount", "reason"],
    BUSINESS_TRIP: ["destination", "startDate", "endDate", "purpose", "cost"],
    OVERTIME: ["date", "startTime", "endTime", "reason"],
    HOLIDAY_WORK: ["date", "startTime", "endTime", "reason"],
    OTHER: ["title", "content"],
};

/**
 * 결재선 사원 선택 — 단일 모드 EmployeeAutocomplete 를 호출하는 얇은 어댑터.
 *
 * <p>외부 list 누적 패턴: 한 명 선택할 때마다 즉시 {@code onSelect(emp)} 콜백으로 외부 list 에 push 하고
 * 자동완성 입력은 비워두어 다음 선택을 받는다. value 를 항상 null 로 유지해 controlled 입력이 자동 reset 되도록.
 *
 * <p>EmployeeSuggestDto 의 {department, position} 필드를 결재 모달 표시 코드가 사용하는
 * {deptName, positionName} 별칭으로 매핑해서 push — 다른 표시 부분(라인 852, 933) 변경 없이 호환.
 */
function ApproverPicker({ excluded, onSelect, placeholder }) {
    return (
        <EmployeeAutocomplete
            value={null}
            onChange={(emp) => {
                if (!emp || typeof emp === "string") return;
                onSelect({
                    ...emp,
                    deptName: emp.department,
                    positionName: emp.position,
                });
            }}
            excludeIds={excluded.map((e) => e.id).filter(Boolean)}
            placeholder={placeholder}
            variant="outlined"
            disableUnderline={false}
            autoFocus
        />
    );
}

function FormFields({ type, formData, onChange, readonly = false, myInfo, holidayApprovals = [], projectChannels = [] }) {
    const data = (() => {
        try {
            return JSON.parse(formData || "{}");
        } catch {
            return {};
        }
    })();

    const set = (key, val) => onChange(JSON.stringify({ ...data, [key]: val }));

    /**
     * startDate/endDate 키는 범위 정합성 자동 보정 (duration 유지 + broken state 방지).
     * 그 외 일반 키는 단순 set.
     *
     * @param {string} key - 변경할 키
     * @param {string} val - 새 값
     * @param {boolean} isDate - 날짜 타입 (date) 여부. false 면 datetime-local
     */
    const setRangeAware = (key, val, isDate) => {
        if (key === "startDate") {
            onChange(JSON.stringify(applyRangeStartChange(data, val, { startKey: "startDate", endKey: "endDate", isDate })));
        } else if (key === "endDate") {
            onChange(JSON.stringify(applyRangeEndChange(data, val, { startKey: "startDate", endKey: "endDate" })));
        } else {
            set(key, val);
        }
    };
    const applicantName = data.applicant || myInfo?.name || "";

    const readonlyBox = (label, value, multiline = false) => (
        <Box key={label}>
            <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.3 }}>{label}</Typography>
            <Typography
                sx={{
                    fontSize: 14,
                    color: "#1e293b",
                    p: "8px 12px",
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 1.5,
                    minHeight: multiline ? 80 : "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                }}
            >
                {value || "—"}
            </Typography>
        </Box>
    );

    const field = (label, key, type_ = "text", multiline = false, required_ = true) =>
        readonly ? (
            readonlyBox(label, data[key], multiline)
        ) : (
            <FormField key={key} label={label} required={required_}>
                <TextField
                    size="small"
                    fullWidth
                    required={required_}
                    type={type_}
                    multiline={multiline}
                    minRows={multiline ? 3 : undefined}
                    value={data[key] || ""}
                    onChange={(e) => setRangeAware(key, e.target.value, type_ === "date")}
                    placeholder={multiline ? `${label}을 입력하세요` : `${label}을 입력하세요`}
                />
            </FormField>
        );

    const toComma = (v) => {
        const num = String(v || "")
            .replace(/,/g, "")
            .replace(/[^\d]/g, "");
        if (!num) return "";
        return Number(num).toLocaleString("ko-KR");
    };

    const numberField = (label, key) =>
        readonly ? (
            readonlyBox(label, data[key] ? `${toComma(data[key])} 원` : "")
        ) : (
            <FormField key={key} label={label} required>
                <TextField
                    size="small"
                    fullWidth
                    required
                    value={toComma(data[key])}
                    onChange={(e) => {
                        const raw = e.target.value.replace(/,/g, "").replace(/[^\d]/g, "");
                        set(key, raw);
                    }}
                    inputProps={{ inputMode: "numeric" }}
                    placeholder="0"
                />
            </FormField>
        );

    const select = (label, key, options) =>
        readonly ? (
            readonlyBox(label, data[key])
        ) : (
            <FormField key={key} label={label} required>
                <ComboBox clearable={false} placeholder={`${label}을 선택하세요`}
                    options={options.map((o) => ({ value: o, label: o }))}
                    value={data[key] || ""}
                    onChange={(v) => set(key, v)} />
            </FormField>
        );

    // 프로젝트 채널 선택 (선택 사항) — 값이 없으면 "선택 안 함". channelId + channelName 을 함께 저장.
    const channelField = (label = "프로젝트 채널") =>
        readonly ? (
            readonlyBox(label, data.channelName)
        ) : (
            <FormField key="channelId" label={label}>
                <ComboBox
                    placeholder="선택 안 함"
                    options={[
                        { value: "", label: "선택 안 함" },
                        ...projectChannels.map((c) => ({ value: c.id, label: c.name })),
                    ]}
                    value={data.channelId || ""}
                    onChange={(v) => {
                        const ch = projectChannels.find((c) => String(c.id) === String(v));
                        onChange(JSON.stringify({ ...data, channelId: v || null, channelName: ch ? ch.name : "" }));
                    }} />
            </FormField>
        );

    // 거래처 선택 (마스터 검색 우선, 없으면 자유 입력) — vendorName + vendorId 저장.
    const vendorField = (label = "거래처") =>
        readonly ? (
            readonlyBox(label, data.vendorName)
        ) : (
            <FormField key="vendorId" label={label}>
                <VendorAutocomplete
                    freeSolo
                    value={data.vendorId || null}
                    initialName={data.vendorName || ""}
                    onChange={(id, vendorOrText) => {
                        if (typeof vendorOrText === "string") {
                            onChange(JSON.stringify({ ...data, vendorName: vendorOrText, vendorId: null }));
                        } else if (vendorOrText) {
                            onChange(JSON.stringify({ ...data, vendorName: vendorOrText.name, vendorId: id }));
                        } else {
                            onChange(JSON.stringify({ ...data, vendorName: "", vendorId: null }));
                        }
                    }}
                    label={null}
                    helperText="등록된 거래처에서 선택하거나 그대로 입력하세요" />
            </FormField>
        );

    const datetimeField = (label, key) =>
        readonly ? (
            readonlyBox(label, data[key] ? dayjs(data[key]).format("YYYY.MM.DD HH:mm") : "")
        ) : (
            <FormField key={key} label={label} required>
                <TextField
                    size="small"
                    fullWidth
                    required
                    type="datetime-local"
                    value={data[key] || ""}
                    onChange={(e) => setRangeAware(key, e.target.value, false)}
                />
            </FormField>
        );

    const applicantRow = (
        <Box key="applicant">
            <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.3 }}>신청자</Typography>
            <Box
                sx={{
                    p: "8px 12px",
                    bgcolor: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    borderRadius: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                <Avatar sx={{ width: 22, height: 22, fontSize: 11, bgcolor: "#2563eb" }}>{applicantName?.[0]}</Avatar>
                <Typography sx={{ fontSize: 14, color: "#1e293b", fontWeight: 600 }}>
                    {applicantName || "—"}
                    {myInfo?.positionName ? (
                        <Box component="span" sx={{ fontWeight: 400, color: "#64748b", fontSize: 12, ml: 1 }}>
                            {myInfo.positionName}
                            {myInfo.deptName ? ` · ${myInfo.deptName}` : ""}
                        </Box>
                    ) : null}
                </Typography>
            </Box>
        </Box>
    );

    switch (type) {
        case "VACATION": {
            const vt = data.vacationType;
            const isAllDay   = !vt || vt === "연차";
            const isHalfDay  = vt === "반차";
            const isCompDay  = vt === "대체휴무";
            const isHourly   = vt === "시간연차";
            const isDatetime = vt && !isAllDay && !isHalfDay && !isCompDay && !isHourly; // 병가 등

            const summaryBox = (text, color = "#0369a1", bg = "#f0f9ff", border = "#bae6fd") => (
                <Box sx={{ px: 1.5, py: 1, bgcolor: bg, borderRadius: 1.5, border: `1px solid ${border}` }}>
                    <Typography sx={{ fontSize: 12, color, fontWeight: 700 }}>{text}</Typography>
                </Box>
            );

            const summary = (() => {
                if (isCompDay || isHourly) return null;
                if (isHalfDay) {
                    if (!data.startDate || !data.halfDayType) return null;
                    return `${data.startDate}  ·  ${data.halfDayType} 반차`;
                }
                if (!data.startDate) return null;
                if (isAllDay) {
                    const end = data.endDate || data.startDate;
                    const days = dayjs(end).diff(dayjs(data.startDate), "day") + 1;
                    return data.startDate === end ? `${data.startDate}  ·  1일` : `${data.startDate} ~ ${end}  ·  ${days}일`;
                }
                if (!isDatetime || !data.endDate) return data.startDate ? dayjs(data.startDate).format("YYYY.MM.DD HH:mm") : null;
                const s = dayjs(data.startDate);
                const e = dayjs(data.endDate);
                const mins = e.diff(s, "minute");
                if (mins <= 0) return `${s.format("YYYY.MM.DD HH:mm")} ~ ${e.format("YYYY.MM.DD HH:mm")}`;
                const h = Math.floor(mins / 60), m = mins % 60;
                const dur = h > 0 ? (m > 0 ? `${h}시간 ${m}분` : `${h}시간`) : `${m}분`;
                return `${s.format("YYYY.MM.DD HH:mm")} ~ ${e.format("YYYY.MM.DD HH:mm")}  ·  ${dur}`;
            })();

            return (
                <Stack spacing={1.5}>
                    {applicantRow}
                    {select("휴가 종류", "vacationType", ["연차", "반차", "시간연차", "병가", "대체휴무"])}

                    {/* 연차: 시작일 ~ 종료일 */}
                    {isAllDay && (
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Box sx={{ flex: 1 }}>{field("시작일", "startDate", "date")}</Box>
                            <Box sx={{ flex: 1 }}>{field("종료일", "endDate", "date")}</Box>
                        </Box>
                    )}

                    {/* 반차: 단일 날짜 + 오전/오후 토글 */}
                    {isHalfDay && (
                        <>
                            {field("날짜", "startDate", "date")}
                            {readonly ? (
                                readonlyBox("반차 구분", data.halfDayType)
                            ) : (
                                <Box>
                                    <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.5 }}>반차 구분 *</Typography>
                                    <Stack direction="row" spacing={1}>
                                        {["오전", "오후"].map(t => (
                                            <Box key={t} onClick={() => set("halfDayType", t)} sx={{
                                                flex: 1, py: 1, textAlign: "center", borderRadius: 1.5, cursor: "pointer",
                                                border: `2px solid ${data.halfDayType === t ? "#2563eb" : "#e2e8f0"}`,
                                                bgcolor: data.halfDayType === t ? "#dbeafe" : "#fafafa",
                                                transition: "all 0.15s",
                                                "&:hover": { borderColor: "#60a5fa", bgcolor: "#eff6ff" },
                                            }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: data.halfDayType === t ? 700 : 500, color: data.halfDayType === t ? "#2563eb" : "#64748b" }}>
                                                    {t}반차
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </>
                    )}

                    {/* 병가·경조사·기타: 날짜+시간 일체형 */}
                    {isDatetime && (
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Box sx={{ flex: 1 }}>{datetimeField("시작 일시", "startDate")}</Box>
                            <Box sx={{ flex: 1 }}>{datetimeField("종료 일시", "endDate")}</Box>
                        </Box>
                    )}

                    {/* 시간연차: 날짜 + 시작/종료 시간 → 시간 단위 연차 사용 */}
                    {isHourly && (() => {
                        const setTimeHours = (key, val) => {
                            const next = { ...data, [key]: val };
                            if (next.startTime && next.endTime) {
                                const mins = dayjs(`2000-01-01T${next.endTime}`).diff(dayjs(`2000-01-01T${next.startTime}`), "minute");
                                next.hours = mins > 0 ? Math.round(mins / 60 * 10) / 10 : 0;
                            }
                            onChange(JSON.stringify(next));
                        };
                        const hrs = Number(data.hours) || 0;
                        return (
                            <>
                                {field("날짜", "startDate", "date")}
                                <Stack direction="row" spacing={1}>
                                    <FormField label="시작 시간" required>
                                        <TextField size="small" fullWidth required type="time" value={data.startTime || ""}
                                            onChange={(e) => setTimeHours("startTime", e.target.value)}
                                             />
                                    </FormField>
                                    <FormField label="종료 시간" required>
                                        <TextField size="small" fullWidth required type="time" value={data.endTime || ""}
                                            onChange={(e) => setTimeHours("endTime", e.target.value)}
                                             />
                                    </FormField>
                                </Stack>
                                {hrs > 0 && summaryBox(
                                    `${data.startDate || ""} ${data.startTime || ""}~${data.endTime || ""} · ${hrs}시간 연차 사용`,
                                    "#0369a1", "#f0f9ff", "#bae6fd",
                                )}
                            </>
                        );
                    })()}

                    {/* 대체휴무: 보상 방식(휴가/현금) + 사용 단위(1일/반차) */}
                    {isCompDay && (() => {
                        const redeem = data.redeemType || "LEAVE";
                        const tSx = (active) => ({
                            flex: 1, py: 1, textAlign: "center", borderRadius: 1.5, cursor: "pointer",
                            border: `2px solid ${active ? "#7c3aed" : "#e2e8f0"}`,
                            bgcolor: active ? "#ede9fe" : "#fafafa", transition: "all 0.15s",
                            "&:hover": { borderColor: "#a78bfa", bgcolor: "#f5f3ff" },
                        });
                        const tTxt = (active) => ({ fontSize: 13, fontWeight: active ? 700 : 500, color: active ? "#6d28d9" : "#64748b" });
                        const amount = data.hours === 4 ? "50,000원" : "100,000원";
                        return (
                            <>
                                <Box>
                                    <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.5 }}>보상 방식 *</Typography>
                                    <Stack direction="row" spacing={1}>
                                        {[["LEAVE", "휴가 사용"], ["CASH", "현금 정산"]].map(([v, l]) => (
                                            <Box key={v} onClick={() => set("redeemType", v)} sx={tSx(redeem === v)}>
                                                <Typography sx={tTxt(redeem === v)}>{l}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.5 }}>사용 단위 *</Typography>
                                    <Stack direction="row" spacing={1}>
                                        {[["1일", 8], ["반차", 4]].map(([u, h]) => (
                                            <Box key={u} onClick={() => onChange(JSON.stringify({ ...data, unit: u, hours: h }))} sx={tSx(data.unit === u)}>
                                                <Typography sx={tTxt(data.unit === u)}>{u} ({h}시간)</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                                {redeem !== "CASH" && field("날짜", "startDate", "date")}
                                {/* 대체근무(휴일근무) 결재 첨부 — 본인의 승인된 휴일근무 결재를 근거로 연결 */}
                                {holidayApprovals.length > 0 && (
                                    <Box>
                                        <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.5 }}>대체근무(휴일근무) 결재 첨부 (선택)</Typography>
                                        <Stack spacing={0.5}>
                                            {holidayApprovals.map((a) => {
                                                const hd = a.form || {};
                                                const ids = data.sourceApprovalIds || [];
                                                const sel = ids.includes(a.id);
                                                return (
                                                    <Box key={a.id} onClick={() => {
                                                        const next = sel ? ids.filter((x) => x !== a.id) : [...ids, a.id];
                                                        const text = holidayApprovals.filter((x) => next.includes(x.id)).map((x) => {
                                                            const d = x.form || {};
                                                            return `${d.date || ""}(${d.hours || "?"}h)`;
                                                        }).join(", ");
                                                        onChange(JSON.stringify({ ...data, sourceApprovalIds: next, sourceApprovalText: text }));
                                                    }} sx={{
                                                        px: 1, py: 0.6, borderRadius: 1, cursor: "pointer",
                                                        border: `1.5px solid ${sel ? "#7c3aed" : "#e2e8f0"}`,
                                                        bgcolor: sel ? "#ede9fe" : "#fff",
                                                        "&:hover": { borderColor: "#a78bfa" },
                                                    }}>
                                                        <Typography sx={{ fontSize: 12, fontWeight: sel ? 700 : 500, color: sel ? "#6d28d9" : "#475569" }}>
                                                            {sel ? "✓ " : ""}{a.title || "휴일근무신청"} · {hd.date || ""} {hd.hours || "?"}시간
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                )}
                                {data.unit && summaryBox(
                                    redeem === "CASH"
                                        ? `현금 정산 · 대체휴무 ${data.unit} · ${amount}`
                                        : `${data.startDate || "(날짜 미선택)"} · 대체휴무 ${data.unit} 사용`,
                                    "#6d28d9", "#f5f3ff", "#ddd6fe",
                                )}
                            </>
                        );
                    })()}

                    {summary && summaryBox(summary)}
                    {field("사유", "reason", "text", true)}
                </Stack>
            );
        }

        case "PAYMENT":
            return (
                <Stack spacing={1.5}>
                    {applicantRow}
                    {/* 거래처 — 마스터에서 검색 우선, 없으면 그대로 직접 입력. */}
                    {readonly ? (
                        readonlyBox("거래처명", data.vendor)
                    ) : (
                        <FormField label="거래처명" required>
                            <VendorAutocomplete
                                freeSolo
                                value={data.vendorId || null}
                                initialName={data.vendor || ""}
                                onChange={(id, vendorOrText) => {
                                    if (typeof vendorOrText === "string") {
                                        // 자유 입력 — 마스터에 등록 안 된 거래처
                                        onChange(JSON.stringify({
                                            ...data, vendor: vendorOrText, vendorId: null,
                                        }));
                                    } else if (vendorOrText) {
                                        // 마스터 선택
                                        onChange(JSON.stringify({
                                            ...data, vendor: vendorOrText.name, vendorId: id,
                                        }));
                                    } else {
                                        onChange(JSON.stringify({
                                            ...data, vendor: "", vendorId: null,
                                        }));
                                    }
                                }}
                                label={null}
                                helperText="등록된 거래처에서 선택하거나 그대로 입력하세요" />
                        </FormField>
                    )}
                    {channelField("프로젝트 채널")}
                    {numberField("금액 (원)", "amount")}
                    {field("지급 예정일", "paymentDate", "date")}
                    {field("지급 내용", "description", "text", true)}
                </Stack>
            );

        case "PURCHASE":
            return (
                <Stack spacing={1.5}>
                    {applicantRow}
                    {field("품목명", "item")}
                    {field("수량", "quantity", "number")}
                    {numberField("금액 (원)", "amount")}
                    {vendorField("거래처")}
                    {channelField("프로젝트 채널")}
                    {field("구매 사유", "reason", "text", true)}
                    {field("구매처 URL (선택)", "vendorUrl", "text", false, false)}
                </Stack>
            );

        case "BUSINESS_TRIP": {
            const btSummary = (() => {
                if (!data.startDate || !data.endDate) return null;
                const s = dayjs(data.startDate), e = dayjs(data.endDate);
                const mins = e.diff(s, "minute");
                if (mins <= 0) return null;
                const days  = Math.floor(mins / 1440);
                const hours = Math.floor((mins % 1440) / 60);
                const rem   = mins % 60;
                const parts = [];
                if (days  > 0) parts.push(`${days}일`);
                if (hours > 0) parts.push(`${hours}시간`);
                if (rem   > 0) parts.push(`${rem}분`);
                const durStr = parts.length > 0 ? `  ·  ${parts.join(" ")}` : "";
                return `${s.format("YYYY.MM.DD HH:mm")} ~ ${e.format("YYYY.MM.DD HH:mm")}${durStr}`;
            })();
            return (
                <Stack spacing={1.5}>
                    {applicantRow}
                    {field("출장지", "destination")}
                    <Stack direction="row" spacing={1}>
                        {datetimeField("출발 일시", "startDate")}
                        {datetimeField("복귀 일시", "endDate")}
                    </Stack>
                    {btSummary && (
                        <Box sx={{ px: 1.5, py: 1, bgcolor: "#fdf4ff", borderRadius: 1.5, border: "1px solid #bfdbfe" }}>
                            <Typography sx={{ fontSize: 12, color: "#7e22ce", fontWeight: 700 }}>{btSummary}</Typography>
                        </Box>
                    )}
                    {field("출장 목적", "purpose", "text", true)}
                    {numberField("예상 비용 (원)", "cost")}
                </Stack>
            );
        }

        case "OVERTIME": {
            const otSummary = (() => {
                if (!data.date || !data.startTime || !data.endTime) return null;
                const s = dayjs(`${data.date}T${data.startTime}`);
                const e = dayjs(`${data.date}T${data.endTime}`);
                const mins = e.diff(s, "minute");
                if (mins <= 0) return null;
                const h = Math.floor(mins / 60), m = mins % 60;
                const dur = h > 0 ? (m > 0 ? `${h}시간 ${m}분` : `${h}시간`) : `${m}분`;
                return `${data.date}  ${data.startTime} – ${data.endTime}  ·  ${dur}`;
            })();
            return (
                <Stack spacing={1.5}>
                    {applicantRow}
                    {field("날짜", "date", "date")}
                    <Stack direction="row" spacing={1}>
                        {field("시작 시간", "startTime", "time")}
                        {field("종료 시간", "endTime", "time")}
                    </Stack>
                    {otSummary && (
                        <Box sx={{ px: 1.5, py: 1, bgcolor: "#fff1f2", borderRadius: 1.5, border: "1px solid #fecdd3" }}>
                            <Typography sx={{ fontSize: 12, color: "#be123c", fontWeight: 700 }}>{otSummary}</Typography>
                        </Box>
                    )}
                    {field("사유", "reason", "text", true)}
                </Stack>
            );
        }

        case "HOLIDAY_WORK": {
            const setTimeHours = (key, val) => {
                const next = { ...data, [key]: val };
                if (next.startTime && next.endTime) {
                    const mins = dayjs(`2000-01-01T${next.endTime}`).diff(dayjs(`2000-01-01T${next.startTime}`), "minute");
                    next.hours = mins > 0 ? Math.round((mins / 60) * 10) / 10 : 0;
                }
                onChange(JSON.stringify(next));
            };
            const hrs = Number(data.hours) || 0;
            const ok = hrs >= 8;
            const timeInput = (label, key) => readonly ? readonlyBox(label, data[key]) : (
                <FormField key={key} label={label} required>
                    <TextField size="small" fullWidth required type="time" value={data[key] || ""}
                        onChange={(e) => setTimeHours(key, e.target.value)}
                        />
                </FormField>
            );
            return (
                <Stack spacing={1.5}>
                    {applicantRow}
                    {field("근무일", "date", "date")}
                    <Stack direction="row" spacing={1}>
                        {timeInput("시작 시간", "startTime")}
                        {timeInput("종료 시간", "endTime")}
                    </Stack>
                    {hrs > 0 && (
                        <Box sx={{ px: 1.5, py: 1, borderRadius: 1.5,
                            bgcolor: ok ? "#f5f3ff" : "#fff1f2", border: `1px solid ${ok ? "#ddd6fe" : "#fecdd3"}` }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: ok ? "#6d28d9" : "#be123c" }}>
                                근무시간 {hrs}시간 {ok ? "· 승인 시 대체휴무 1일(8h) 적립" : "· 8시간 이상만 신청 가능합니다"}
                            </Typography>
                        </Box>
                    )}
                    {field("사유", "reason", "text", true)}
                </Stack>
            );
        }

        default:
            return (
                <Stack spacing={1.5}>
                    {applicantRow}
                    {field("제목", "title")}
                    {field("내용", "content", "text", true)}
                </Stack>
            );
    }
}

export default function CreateApprovalModal({ open, onClose, onCreated, myInfo }) {
    const [step, setStep] = useState(1);
    const [selectedType, setSelectedType] = useState(null);
    const [formData, setFormData] = useState("{}");
    const [title, setTitle] = useState("");
    const [intermediates, setIntermediates] = useState([]);
    const [finalApprover, setFinalApprover] = useState(null);
    const [activePicker, setActivePicker] = useState(null);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [holidayApprovals, setHolidayApprovals] = useState([]); // 본인의 승인된 휴일근무 결재 (대체휴무 첨부용)
    const [projectChannels, setProjectChannels] = useState([]);   // 프로젝트 채널 선택용 (구매품의서·대금지급요청)
    const fileInputRef = useRef();

    useEffect(() => {
        if (!open) return;
        setStep(1);
        setSelectedType(null);
        setFormData("{}");
        setTitle("");
        setIntermediates([]);
        setFinalApprover(null);
        setActivePicker(null);
        setPendingFiles([]);
        // 사원 자동완성은 ApproverPicker(EmployeeAutocomplete) 가 /api/employees/suggest 를 호출하므로
        // 전체 사원 prefetch 불필요.
    }, [open]);

    // 대체휴무 결재에 첨부할 "본인의 승인된 휴일근무 결재" 로드
    useEffect(() => {
        if (!open) return;
        approvalApi.sent(0, 200)
            .then((res) => {
                const list = res?.content ?? (Array.isArray(res) ? res : []);
                setHolidayApprovals(list.filter((a) => a.type === "HOLIDAY_WORK" && a.status === "APPROVED"));
            })
            .catch(() => setHolidayApprovals([]));
    }, [open]);

    // 프로젝트 채널 로드 (구매품의서·대금지급요청에서 프로젝트 선택용) — 내가 속한 채널 중 PROJECT 타입만
    useEffect(() => {
        if (!open) return;
        channelApi.list()
            .then((res) => {
                const list = Array.isArray(res) ? res : (res?.data || []);
                setProjectChannels(list.filter((c) => c.type === "PROJECT" && !c.archived));
            })
            .catch(() => setProjectChannels([]));
    }, [open]);

    useEffect(() => {
        if (!myInfo || !selectedType) return;
        try {
            const d = JSON.parse(formData || "{}");
            if (!d.applicant) {
                setFormData(JSON.stringify({ ...d, applicant: myInfo.name }));
            }
        } catch {}
    }, [myInfo, selectedType]);

    const handleSelectType = (type) => {
        setSelectedType(type);
        setFormData(JSON.stringify({ applicant: myInfo?.name || "" }));
        setStep(2);
    };

    const isStep2Valid = () => {
        if (!selectedType) return false;
        try {
            const d = JSON.parse(formData || "{}");
            let required = [...(REQUIRED_FIELDS[selectedType] || [])];
            if (selectedType === "VACATION") {
                if (d.vacationType === "반차") {
                    required = ["vacationType", "startDate", "halfDayType", "reason"];
                } else if (d.vacationType === "대체휴무") {
                    // 대체휴무: 사용 단위 필수, 휴가일 땐 날짜 필수, 현금이면 날짜 불필요
                    required = (d.redeemType || "LEAVE") === "CASH"
                        ? ["vacationType", "unit", "reason"]
                        : ["vacationType", "unit", "startDate", "reason"];
                } else if (d.vacationType === "시간연차") {
                    // 시간연차: 날짜 + 시작/종료 시간 (시간 자동 계산)
                    required = ["vacationType", "startDate", "startTime", "endTime", "reason"];
                } else if (d.vacationType && !["연차"].includes(d.vacationType)) {
                    // 병가·경조사·기타: endDate 필요 (startDate에 시간 포함)
                    required = ["vacationType", "startDate", "endDate", "reason"];
                }
            }
            // 시간연차: 종료>시작 이어서 계산된 시간이 0보다 커야 함
            if (selectedType === "VACATION" && d.vacationType === "시간연차" && (Number(d.hours) || 0) <= 0) return false;
            // 휴일근무: 근무 8시간 이상만 신청 가능 (서버도 동일하게 차단)
            if (selectedType === "HOLIDAY_WORK" && (Number(d.hours) || 0) < 8) return false;
            return required.every((k) => d[k] !== undefined && String(d[k]).trim() !== "");
        } catch {
            return false;
        }
    };

    const handleSubmit = async () => {
        if (!finalApprover) {
            notify("최종 승인자를 지정해주세요.", "warning");
            return;
        }

        setSubmitting(true);
        try {
            const approverIds = [...intermediates.map((a) => a.id), finalApprover.id];

            // 내부 formData(JSON 문자열)를 타입별 form 객체로 변환해 전송.
            const parsed = (() => { try { return JSON.parse(formData || "{}"); } catch { return {}; } })();
            const form = { ...parsed, type: selectedType };

            const res = await approvalApi.create({
                title: title.trim() || null,
                approverIds,
                form,
            });

            const approvalId = (res?.data ?? res)?.id;
            if (!approvalId) throw new Error("결재 ID를 받지 못했습니다. 서버 응답을 확인해주세요.");

            for (const f of pendingFiles) {
                await approvalApi.uploadAttachment(approvalId, f);
            }

            notify("결재가 올려졌습니다.", "success");
            onCreated();
            onClose();
        } catch (e) {
            notify(getApiErrorMessage(e, "결재 생성 실패"), "error");
        } finally {
            setSubmitting(false);
        }
    };

    const allExcluded = [...intermediates, ...(finalApprover ? [finalApprover] : [])];
    const step3Valid = !!finalApprover;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { maxHeight: "92vh" } }}>
            <Box
                sx={{
                    px: 3,
                    pt: 2.5,
                    pb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #f1f5f9",
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    {[1, 2, 3].map((s) => (
                        <Box key={s} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Box
                                sx={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 12,
                                    fontWeight: 700,
                                    bgcolor: step === s ? "#2563eb" : step > s ? "#2563eb" : "#e2e8f0",
                                    color: step >= s ? "#fff" : "#94a3b8",
                                }}
                            >
                                {step > s ? "✓" : s}
                            </Box>
                            {s < 3 && <Box sx={{ width: 20, height: 2, bgcolor: step > s ? "#2563eb" : "#e2e8f0" }} />}
                        </Box>
                    ))}
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#1e293b", ml: 0.5 }}>
                        {step === 1 ? "양식 선택" : step === 2 ? "내용 작성" : "결재 라인 설정"}
                    </Typography>
                </Stack>
                <IconButton size="small" onClick={onClose}>
                    <CloseRoundedIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>

            <DialogContent sx={{ pt: 2, pb: 1 }}>
                {step === 1 && (
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.5 }}>
                        {FORM_TYPES.map((ft) => (
                            <Box
                                key={ft.type}
                                onClick={() => handleSelectType(ft.type)}
                                sx={{
                                    p: 2,
                                    borderRadius: 1.5,
                                    border: `1.5px solid ${selectedType === ft.type ? ft.color : "#e2e8f0"}`,
                                    cursor: "pointer",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 1,
                                    bgcolor: selectedType === ft.type ? `${ft.color}0f` : "#fff",
                                    "&:hover": { borderColor: ft.color, bgcolor: `${ft.color}0a` },
                                    transition: "all 0.15s",
                                }}
                            >
                                <Box sx={{ color: ft.color }}>{ft.icon}</Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b", textAlign: "center" }}>
                                    {ft.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {step === 2 && selectedType && (
                    <Stack spacing={2}>
                        <FormField label="제목">
                            <TextField
                                size="small"
                                fullWidth
                                placeholder={`${FORM_TYPES.find((f) => f.type === selectedType)?.label || "결재"} 제목을 입력하세요`}
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                        </FormField>

                        <Divider />

                        <FormFields type={selectedType} formData={formData} onChange={setFormData} readonly={false} myInfo={myInfo} holidayApprovals={holidayApprovals} projectChannels={projectChannels} />

                        <Box sx={{ pt: 0.5 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#64748b", mb: 0.75 }}>
                                첨부파일 (선택)
                            </Typography>

                            <Stack direction="row" flexWrap="wrap" gap={0.75} mb={0.75}>
                                {pendingFiles.map((f, i) => (
                                    <Chip
                                        key={i}
                                        icon={<InsertDriveFileRoundedIcon sx={{ fontSize: 13 }} />}
                                        label={f.name}
                                        size="small"
                                        onDelete={() => setPendingFiles(pendingFiles.filter((_, j) => j !== i))}
                                        sx={{ maxWidth: 200, fontSize: 12, bgcolor: "#f1f5f9" }}
                                    />
                                ))}
                            </Stack>

                            <ActionButton
                                variant="secondary"
                                startIcon={<AttachFileRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => fileInputRef.current?.click()}
                                label="파일 추가"
                                sx={{ fontSize: 13, color: "#64748b", borderColor: "transparent",
                                    "&:hover": { borderColor: "transparent", bgcolor: "#f1f5f9" } }}
                            />

                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                multiple
                                onChange={(e) => setPendingFiles((p) => [...p, ...Array.from(e.target.files)])}
                            />
                        </Box>
                    </Stack>
                )}

                {step === 3 && (
                    <Stack spacing={2.5}>
                        <Box sx={{ p: 1.5, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid #e2e8f0" }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", mb: 1 }}>결재 흐름</Typography>
                            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.75}>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: "#dbeafe", color: "#1d4ed8", fontSize: 12 }}>
                                        {myInfo?.name?.[0]}
                                    </Avatar>
                                    <Typography sx={{ fontSize: 10, color: "#64748b" }}>{myInfo?.name}</Typography>
                                    <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>신청</Typography>
                                </Box>

                                {intermediates.map((a) => (
                                    <React.Fragment key={a.id}>
                                        <Box sx={{ width: 16, height: 2, bgcolor: "#2563eb" }} />
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
                                            <Avatar sx={{ width: 32, height: 32, bgcolor: "#2563eb", color: "#fff", fontSize: 12 }}>
                                                {a.name?.[0]}
                                            </Avatar>
                                            <Typography sx={{ fontSize: 10, color: "#2563eb", fontWeight: 600 }}>
                                                {a.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>중간</Typography>
                                        </Box>
                                    </React.Fragment>
                                ))}

                                {finalApprover ? (
                                    <>
                                        <Box sx={{ width: 16, height: 2, bgcolor: "#16a34a" }} />
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.3 }}>
                                            <Avatar sx={{ width: 32, height: 32, bgcolor: "#16a34a", color: "#fff", fontSize: 12 }}>
                                                {finalApprover.name?.[0]}
                                            </Avatar>
                                            <Typography sx={{ fontSize: 10, color: "#16a34a", fontWeight: 700 }}>
                                                {finalApprover.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>최종</Typography>
                                        </Box>
                                    </>
                                ) : (
                                    <>
                                        <Box sx={{ width: 16, height: 2, bgcolor: "#e2e8f0" }} />
                                        <Box
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: "50%",
                                                border: "2px dashed #e2e8f0",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <Typography sx={{ fontSize: 16, color: "#cbd5e1" }}>?</Typography>
                                        </Box>
                                    </>
                                )}
                            </Stack>
                        </Box>

                        <Box>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
                                    중간 결재자{" "}
                                    <Box component="span" sx={{ color: "#94a3b8", fontWeight: 400 }}>
                                        (선택, 복수 가능)
                                    </Box>
                                </Typography>

                                <ActionButton
                                    variant={activePicker === "intermediate" ? "primary" : "secondary"}
                                    startIcon={<AddRoundedIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => setActivePicker(activePicker === "intermediate" ? null : "intermediate")}
                                    sx={{ fontSize: 12, py: 0.3,
                                        ...(activePicker === "intermediate"
                                            ? {}
                                            : { color: "#2563eb", borderColor: "#93c5fd",
                                                "&:hover": { color: "#2563eb", borderColor: "#93c5fd", bgcolor: "#eff6ff" } }) }}
                                >
                                    추가
                                </ActionButton>
                            </Stack>

                            {intermediates.length > 0 ? (
                                <Stack spacing={0.5} mb={1}>
                                    {intermediates.map((a, i) => (
                                        <Box
                                            key={a.id}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                px: 1.5,
                                                py: 0.75,
                                                bgcolor: "#eff6ff",
                                                borderRadius: 1.5,
                                                border: "1px solid #dbeafe",
                                            }}
                                        >
                                            <Typography sx={{ fontSize: 12, color: "#2563eb", fontWeight: 700, mr: 0.5 }}>
                                                {i + 1}
                                            </Typography>
                                            <Avatar sx={{ width: 24, height: 24, bgcolor: "#2563eb", fontSize: 11 }}>
                                                {a.name?.[0]}
                                            </Avatar>
                                            <Box flex={1}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{a.name}</Typography>
                                                <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                                                    {a.deptName} · {a.positionName}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => setIntermediates(intermediates.filter((_, j) => j !== i))}
                                                sx={{ p: 0.3 }}
                                            >
                                                <CloseRoundedIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography sx={{ fontSize: 12, color: "#94a3b8", mb: 1 }}>
                                    중간 결재자 없이 최종 승인자에게 바로 전달됩니다.
                                </Typography>
                            )}

                            {activePicker === "intermediate" && (
                                <ApproverPicker
                                    excluded={allExcluded}
                                    onSelect={(emp) => setIntermediates([...intermediates, emp])}
                                    placeholder="중간 결재자 검색"
                                />
                            )}
                        </Box>

                        <Divider />

                        <Box>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
                                    최종 승인자{" "}
                                    <Box component="span" sx={{ color: "#ef4444", fontWeight: 700 }}>
                                        *
                                    </Box>
                                    <Box component="span" sx={{ color: "#94a3b8", fontWeight: 400 }}>
                                        {" "}
                                        (1명, 필수)
                                    </Box>
                                </Typography>

                                {!finalApprover && (
                                    <ActionButton
                                        variant={activePicker === "final" ? "primary" : "secondary"}
                                        startIcon={<AddRoundedIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => setActivePicker(activePicker === "final" ? null : "final")}
                                        sx={{ fontSize: 12, py: 0.3,
                                            ...(activePicker === "final"
                                                ? { bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }
                                                : { color: "#16a34a", borderColor: "#bbf7d0",
                                                    "&:hover": { color: "#16a34a", borderColor: "#bbf7d0", bgcolor: "#f0fdf4" } }) }}
                                    >
                                        선택
                                    </ActionButton>
                                )}
                            </Stack>

                            {finalApprover ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        px: 1.5,
                                        py: 0.85,
                                        bgcolor: "#f0fdf4",
                                        borderRadius: 1.5,
                                        border: "1px solid #bbf7d0",
                                    }}
                                >
                                    <Avatar sx={{ width: 28, height: 28, bgcolor: "#16a34a", fontSize: 12 }}>
                                        {finalApprover.name?.[0]}
                                    </Avatar>
                                    <Box flex={1}>
                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#15803d" }}>
                                            {finalApprover.name}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                                            {finalApprover.deptName} · {finalApprover.positionName}
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setFinalApprover(null);
                                            setActivePicker("final");
                                        }}
                                        sx={{ p: 0.3 }}
                                    >
                                        <CloseRoundedIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Typography sx={{ fontSize: 12, color: "#ef4444" }}>최종 승인자를 반드시 선택해주세요.</Typography>
                            )}

                            {activePicker === "final" && !finalApprover && (
                                <Box mt={1}>
                                    <ApproverPicker
                                        excluded={allExcluded}
                                        onSelect={(emp) => {
                                            setFinalApprover(emp);
                                            setActivePicker(null);
                                        }}
                                        placeholder="최종 승인자 검색"
                                    />
                                </Box>
                            )}
                        </Box>
                    </Stack>
                )}
            </DialogContent>

            <Box
                sx={{
                    px: 3,
                    py: 1.5,
                    borderTop: "1px solid #f1f5f9",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <ActionButton
                    variant="secondary"
                    startIcon={false}
                    onClick={() => {
                        if (step > 1) setStep(step - 1);
                        else onClose();
                    }}
                    label={step > 1 ? "이전" : "취소"}
                    sx={{ color: "#64748b", borderColor: "transparent",
                        "&:hover": { borderColor: "transparent", bgcolor: "#f1f5f9" } }}
                />

                {step < 3 ? (
                    <Tooltip title={step === 2 && !isStep2Valid() ? "모든 필수 항목을 입력해주세요." : ""} placement="top">
                        <span>
                            <ActionButton
                                variant="primary"
                                disabled={step === 2 && !isStep2Valid()}
                                onClick={() => setStep(step + 1)}
                                label="다음"
                            />
                        </span>
                    </Tooltip>
                ) : (
                    <Tooltip title={!step3Valid ? "최종 승인자를 선택해주세요." : ""} placement="top">
                        <span>
                            <ActionButton
                                variant="primary"
                                disabled={!step3Valid}
                                loading={submitting}
                                onClick={handleSubmit}
                                startIcon={<SendRoundedIcon sx={{ fontSize: 16 }} />}
                                label="결재 올리기"
                            />
                        </span>
                    </Tooltip>
                )}
            </Box>
        </Dialog>
    );
}
